import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getPermissions } from '../../lib/accessControl';
import { roles } from '../../utils/roles';
import { abortController } from '../../utils/abortController';
import withAuth from '../../lib/withAuth';
import useUser from '../../hooks/useUser';
import useSessionExpire from '../../hooks/useSessionExpire';
import usePageFilterPersistence from '../../hooks/usePageFilterPersistence';
import Backdrop from '@mui/material/Backdrop';
import Loader from '../../components/Loader';
import MaterialReactDataTable from '../../components/MaterialReactDataTable';
import Alert from '../../components/Alert';
import axios from 'axios';

const Requests = ({ logOut = () => {} }) => 
{
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { kyc_id } = useParams();

    /* AUTHENTICATION VARIABLES */
    const currentUser = useUser();

    /* AUTHORIZATION VARIABLES */
    const { isWritable = false } = getPermissions(currentUser?.role, pathname);
    const isGRCRole = currentUser?.role === roles[7];
    const isExecutiveRole = currentUser?.role === roles[19];

    /* SESSION EXPIRY VARIABLES */
    const triggerSessionExpire = useSessionExpire();

    /* PERSISTED FILTERS (redux, keyed by route) */
    // Shared explicitly with MaterialReactDataTable (via the pageKey prop below)
    // so both this page and the table read/write the same persisted slot for
    // the global search value.
    const pageKey = `${pathname}::Requests`;
    const {
        filterValues: persistedFilters,
        setFilter: persistFilter
    } = usePageFilterPersistence(pageKey);
    const hasHydratedGlobalFilterRef = useRef(false);
    const persistDebounceRef = useRef(null);

    /* API VARIABLES */
    const [isLoading, setIsLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("");

    /* DATATABLE VARIABLES */
    const [kycRequestData, setKYCRequestData] = useState({});
    const [toggleDisplayData, setToggleDisplayData] = useState(false);
    const tableRef = useRef();

    /* DATATABLE GLOBAL FILTER VARIABLES */
    const [globalFilterValue, setGlobalFilterValue] = useState("");

    useEffect(() =>
    {
        const fetchData = async () => 
        {
            setIsLoading(true);
            setShowAlert(false);
            setAlertMessage("");
            setAlertType("");

            await axios({
                method: "post",
                url: "/api/GetKYCRequestList",
                data: {
                    filter_value: globalFilterValue !== "" ? globalFilterValue : kyc_id ? kyc_id : ""

                },
                signal: abortController.signal
            })
            .then((response) => 
            {
                setIsLoading(false);
                const { status, data } = response;
        
                if (status === 200) 
                {
                    const kycRequestData = data?.kyc_request_data || {};        
                    setKYCRequestData(kycRequestData);
                }
                else
                {
                    setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            })
            .catch((error) => 
            {
                console.log("Get KYC Request List Api: ", error);
                setIsLoading(false);

                if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
                {
                    return;
                }
                
                const status = error?.response?.status;

                if (status === 403) 
                {
                    triggerSessionExpire();
                } 
                else 
                {
                    setAlertMessage(
                        status === 401
                            ? "Unauthorized access. You do not have the required permissions to perform this action."
                            : status === 429
                            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                            : "An error occurred while processing your request. Please try again later or contact the site administrator."
                    );
                    setAlertType("error");
                    setShowAlert(true);
                
                    if (status === 429) 
                    {
                        setTimeout(logOut, 3000);
                    }
                }
            });
        }
    
        fetchData();

        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [pathname, toggleDisplayData]);

    /* -------- PERSISTED GLOBAL SEARCH: hydrate once on mount -------- */
    // redux-persist rehydrates the store before this component renders, so
    // persistedFilters.globalFilterValue is already available on first mount.
    // Since the fetch effect above only re-runs on pathname/toggleDisplayData
    // changes (not on globalFilterValue), we nudge toggleDisplayData here so
    // a persisted search value is actually applied to the initial fetch.
    useEffect(() => {
        if (hasHydratedGlobalFilterRef.current) return;
        hasHydratedGlobalFilterRef.current = true;

        const persistedValue = persistedFilters?.globalFilterValue;

        if (persistedValue) {
            setGlobalFilterValue(persistedValue);
            setToggleDisplayData((previous) => !previous);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    /* -------- PERSISTED GLOBAL SEARCH: persist on change (debounced) -------- */
    useEffect(() => {
        if (!hasHydratedGlobalFilterRef.current) return;

        if (persistDebounceRef.current) {
            clearTimeout(persistDebounceRef.current);
        }

        persistDebounceRef.current = setTimeout(() => {
            persistFilter('globalFilterValue', globalFilterValue || "");
        }, 400);

        return () => clearTimeout(persistDebounceRef.current);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [globalFilterValue]);

    const handleGlobalFilter = async (rowData) =>
    {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");
        
        await axios({
            method: "post",
            url: "/api/FilterTableData",
            data: { 
                pathname: pathname,
                table_data: rowData,
                filter_value: globalFilterValue
            },
            signal: abortController.signal
        })
        .then((response) => 
        {
            setIsLoading(false);
            const { status, data } = response;
    
            if (status === 200) 
            {
                tableRef?.current?.setFilteredData(data || []);
            }
            else
            {
                setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                setAlertType("error");
                setShowAlert(true);
            }
        })
        .catch((error) => 
        {
            console.log("Filter Table Data Api: ", error);
            setIsLoading(false);

            if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
            {
                return;
            }
            
            const status = error?.response?.status;

            if (status === 403) 
            {
                triggerSessionExpire();
            } 
            else 
            {
                setAlertMessage(
                    status === 401
                        ? "Unauthorized access. You do not have the required permissions to perform this action."
                        : status === 429
                        ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                        : "An error occurred while processing your request. Please try again later or contact the site administrator."
                );
                setAlertType("error");
                setShowAlert(true);
            
                if (status === 429) 
                {
                    setTimeout(logOut, 3000);
                }
            }
        });
    }

    const handleEditKYCRequest = async (id) =>
    {
        if (isGRCRole)
        {
            setIsLoading(true);
            setShowAlert(false);
            setAlertMessage("");
            setAlertType("");
            
            await axios({
                method: "post",
                url: "/api/SetKYCRequestFormLock",
                data: {
                    uuid: id,
                    lock_kyc: true
                },
                signal: abortController.signal
            })
            .then((response) => 
            {
                setIsLoading(false);
                const { status } = response;
        
                if (status === 200) 
                {
                    navigate("/kyc/edit-kyc", { state: { kycId: id, previousPage: "Requests" } });
                }
                else
                {
                    setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            })
            .catch((error) => 
            {
                console.log("Set KYC Request Form Lock Api: ", error);
                setIsLoading(false);

                if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
                {
                    return;
                }
                
                const status = error?.response?.status;

                if (status === 403) 
                {
                    triggerSessionExpire();
                } 
                else 
                {
                    setAlertMessage(
                        status === 401
                            ? "Unauthorized access. You do not have the required permissions to perform this action."
                            : status === 429
                            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                            : "An error occurred while processing your request. Please try again later or contact the site administrator."
                    );
                    setAlertType("error");
                    setShowAlert(true);
                
                    if (status === 429) 
                    {
                        setTimeout(logOut, 3000);
                    }
                }
            });
        }
        else
        {
            navigate("/kyc/edit-kyc", { state: { kycId: id, previousPage: "Requests" } });
        }
    }

    return (
        <div className = "content-container d-flex flex-column container kyc">
            <Alert 
                show = {showAlert}
                message = {alertMessage}
                type = {alertType}
                setShow = {setShowAlert}
            />

            <Backdrop
                sx = {{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open = {isLoading}
            >
                <Loader/>
            </Backdrop>

            <h4 className = "page-heading m-0">
                Requests
            </h4>

            <LocalizationProvider dateAdapter = {AdapterDayjs}>
                <MaterialReactDataTable
                    ref = {tableRef}
                    title = "Requests"
                    pageKey = {pageKey}
                    isWritable = {isWritable && !isExecutiveRole}
                    isLoading = {isLoading}
                    isBusinessUnitSelected = {false}
                    globalFilterValue = {globalFilterValue}
                    counterPartyTypes = {["Customer", "Supplier"]}
                    tableData = {kycRequestData}
                    setIsLoading = {setIsLoading}
                    setShowAlert = {setShowAlert}
                    setAlertMessage = {setAlertMessage}
                    setAlertType = {setAlertType}
                    setGlobalFilterValue = {setGlobalFilterValue}
                    handleEditKYCRequest = {handleEditKYCRequest}
                    handleGlobalFilter = {handleGlobalFilter}
                />
            </LocalizationProvider>
        </div>
    );
}

export default withAuth(Requests)([
    roles[4], 
    roles[5], 
    roles[6], 
    roles[7], 
    roles[10], 
    roles[11], 
    roles[12], 
    roles[13], 
    roles[14],
    roles[15], 
    roles[16], 
    roles[17],
    roles[18],
    roles[19]
]);